import React from "react";
import {
  Building2, Lock, FlaskConical, Factory, Package, Truck, Handshake, Store, Warehouse, Users,
  Megaphone, Share2, BadgeCheck, TrendingUp, Landmark, Globe, PartyPopper, Plane, ShieldAlert,
  Briefcase, FileText, Layers, CircleCheck, CircleDashed, Circle, MinusCircle, Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CATEGORY_BY_ID, ACCENT_CLASSES } from "@/data/agreements/categories";

/**
 * Every `icon` string used in categories.js resolves here. Anything unknown falls back to
 * FileText rather than rendering `undefined` as a component (which throws).
 */
export const ICONS = {
  Building2, Lock, FlaskConical, Factory, Package, Truck, Handshake, Store, Warehouse, Users,
  Megaphone, Share2, BadgeCheck, TrendingUp, Landmark, Globe, PartyPopper, Plane, ShieldAlert,
  Briefcase, FileText, Layers,
};

const FALLBACK_ACCENT = { text: "text-slate-600", bg: "bg-slate-50", ring: "ring-slate-200", dot: "bg-slate-500" };

const BOX = { sm: "h-7 w-7 rounded-md", md: "h-9 w-9 rounded-lg", lg: "h-12 w-12 rounded-xl" };
const GLYPH = { sm: "h-3.5 w-3.5", md: "h-[18px] w-[18px]", lg: "h-6 w-6" };

/** The lucide component for a category id. Never returns undefined. */
export function iconForCategory(categoryId) {
  const cat = CATEGORY_BY_ID[categoryId];
  return ICONS[cat?.icon] || FileText;
}

/** The accent class set for a category id. Never returns undefined. */
export function accentForCategory(categoryId) {
  const cat = CATEGORY_BY_ID[categoryId];
  return ACCENT_CLASSES[cat?.accent] || FALLBACK_ACCENT;
}

/**
 * CategoryIcon — the category's lucide glyph in a tinted, accent-colored square.
 * An unknown or missing `categoryId` renders a neutral FileText box instead of crashing.
 */
export default function CategoryIcon({ categoryId, className, size = "md" }) {
  const Icon = iconForCategory(categoryId);
  const accent = accentForCategory(categoryId);
  const box = BOX[size] || BOX.md;
  const glyph = GLYPH[size] || GLYPH.md;
  const cat = CATEGORY_BY_ID[categoryId];
  return (
    <span
      title={cat?.name}
      className={cn("inline-flex shrink-0 items-center justify-center ring-1", box, accent.bg, accent.text, accent.ring, className)}
    >
      <Icon className={glyph} aria-hidden="true" />
    </span>
  );
}

/** IMPORTANCE ids from stages.js → glyph. */
export const IMPORTANCE_ICON = {
  essential: ShieldAlert,
  common: CircleCheck,
  situational: CircleDashed,
  advanced: Layers,
};

/** CHECKLIST_STATUSES ids from masterChecklist.js → glyph. */
export const STATUS_ICON = {
  "not-started": Circle,
  "in-progress": Clock,
  signed: CircleCheck,
  "not-needed": MinusCircle,
};
